import { jsPDF } from 'jspdf';
import { CONSULTATION_PDF_FONT_FAMILY, applyConsultationPdfFonts } from '../consultations/utils/consultationPdfFont';
import { formatMoney, formatPartyLines, sellerFromInvoice, toDateInput } from './invoiceForm';
import { Invoice } from './types/Invoice';

export type InvoicePdfLabels = {
  invoice: string;
  creditNote: string;
  from: string;
  billTo: string;
  taxId: string;
  issueDate: string;
  dueDate: string;
  relatedInvoice: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxPercent: string;
  lineTotal: string;
  subtotal: string;
  taxTotal: string;
  total: string;
  paid: string;
  remaining: string;
  overpaid: string;
  customerNote: string;
  page: string;
  of: string;
};

const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(normalized, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const HEADER_BG = hexToRgb('#152033');
const HEADER_TEXT = hexToRgb('#ffffff');
const MUTED = hexToRgb('#64748b');
const INK = hexToRgb('#152033');
const LINE = hexToRgb('#d8e0eb');
const ROW_ALT = hexToRgb('#f4f7fb');
const TABLE_HEADER = hexToRgb('#e8eef6');

export const sanitizeInvoicePdfFileName = (number?: string, id?: string) => {
  const base = (number || `invoice-${(id || 'document').slice(0, 8)}`)
    .replace(/[<>:"/\\|?*]+/g, '-')
    .replace(/\s+/g, '-')
    .slice(0, 80);
  return `${base}.pdf`;
};

const ensureSpace = (doc: jsPDF, y: number, needed: number, bottomMargin: number) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - bottomMargin) {
    doc.addPage();
    return { y: 18, wrapped: true };
  }
  return { y, wrapped: false };
};

const writeLines = (doc: jsPDF, lines: string[], x: number, y: number, lineHeight: number) => {
  lines.forEach((line, index) => {
    doc.text(line, x, y + index * lineHeight);
  });
  return y + lines.length * lineHeight;
};

export const renderInvoicePdf = async (invoice: Invoice, labels: InvoicePdfLabels, statusLabel: string) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  await applyConsultationPdfFonts(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 14;
  const right = pageWidth - 14;
  const contentWidth = right - left;
  const lineHeight = 4.6;
  const bottomMargin = 18;
  const title = invoice.documentType === 'creditNote' ? labels.creditNote : labels.invoice;
  const numberLabel = invoice.number || statusLabel;

  doc.setFillColor(...HEADER_BG);
  doc.rect(0, 0, pageWidth, 28, 'F');
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...HEADER_TEXT);
  doc.text(title, left, 12);
  doc.setFontSize(11);
  doc.text(numberLabel, left, 20);
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
  doc.setFontSize(9);
  doc.text(statusLabel, right, 12, { align: 'right' });
  if (invoice.currency) {
    doc.text(invoice.currency, right, 20, { align: 'right' });
  }

  let y = 38;
  doc.setTextColor(...INK);
  const columnWidth = (contentWidth - 10) / 2;
  const sellerLines = formatPartyLines(sellerFromInvoice(invoice, labels.taxId));
  const billToLines = formatPartyLines({
    name: invoice.billToName,
    email: invoice.billToEmail,
    phone: invoice.billToPhone,
    addressLine1: invoice.billToAddressLine1,
    addressLine2: invoice.billToAddressLine2,
    city: invoice.billToCity,
    stateOrProvince: invoice.billToStateOrProvince,
    postalCode: invoice.billToPostalCode,
    country: invoice.billToCountry,
  });

  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(labels.from.toUpperCase(), left, y);
  doc.text(labels.billTo.toUpperCase(), left + columnWidth + 10, y);
  y += 6;
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  const sellerBlock = sellerLines.length > 0 ? sellerLines : ['-'];
  const billToBlock = billToLines.length > 0 ? billToLines : ['-'];
  const partyHeight = Math.max(sellerBlock.length, billToBlock.length) * lineHeight;
  writeLines(doc, sellerBlock, left, y, lineHeight);
  writeLines(doc, billToBlock, left + columnWidth + 10, y, lineHeight);
  y += partyHeight + 8;

  const meta: Array<[string, string]> = [
    [labels.issueDate, toDateInput(invoice.issueDate) || '-'],
    [labels.dueDate, toDateInput(invoice.dueDate) || '-'],
  ];
  if (invoice.relatedInvoiceNumber || invoice.relatedInvoiceId) {
    meta.push([labels.relatedInvoice, invoice.relatedInvoiceNumber || invoice.relatedInvoiceId || '-']);
  }

  doc.setFontSize(9);
  meta.forEach(([label, value], index) => {
    const x = left + (index % 3) * (contentWidth / 3);
    if (index % 3 === 0 && index > 0) y += 10;
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    doc.setTextColor(...MUTED);
    doc.text(label.toUpperCase(), x, y);
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
    doc.setTextColor(...INK);
    doc.text(value, x, y + 5);
  });
  y += 14;

  const columns = [
    { key: 'description', width: 82 },
    { key: 'quantity', width: 18 },
    { key: 'unitPrice', width: 28 },
    { key: 'taxPercent', width: 20 },
    { key: 'lineTotal', width: 34 },
  ] as const;
  const tableWidth = columns.reduce((sum, column) => sum + column.width, 0);
  const headerHeight = 8;

  const drawTableHeader = (originY: number) => {
    doc.setFillColor(...TABLE_HEADER);
    doc.setDrawColor(...LINE);
    doc.rect(left, originY, tableWidth, headerHeight, 'FD');
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    let x = left;
    columns.forEach(column => {
      const label = labels[column.key];
      if (column.key === 'description') {
        doc.text(label, x + 2, originY + 5.2);
      } else {
        doc.text(label, x + column.width - 2, originY + 5.2, { align: 'right' });
      }
      x += column.width;
    });
    return originY + headerHeight;
  };

  ({ y } = ensureSpace(doc, y, headerHeight + 12, bottomMargin));
  y = drawTableHeader(y);

  const sortedLines = [...invoice.lines].sort((a, b) => a.sequence - b.sequence);
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...INK);

  sortedLines.forEach((line, index) => {
    const description = [line.name, line.unit ? `(${line.unit})` : '', line.description]
      .map(value => (value || '').trim())
      .filter(Boolean)
      .join('\n');
    const nameLines = doc.splitTextToSize(description || '-', columns[0].width - 4) as string[];
    const rowHeight = Math.max(8, nameLines.length * lineHeight + 4);
    const spaced = ensureSpace(doc, y, rowHeight, bottomMargin);
    y = spaced.y;
    if (spaced.wrapped) {
      y = drawTableHeader(y);
    }

    if (index % 2 === 1) {
      doc.setFillColor(...ROW_ALT);
      doc.rect(left, y, tableWidth, rowHeight, 'F');
    }
    doc.setDrawColor(...LINE);
    doc.rect(left, y, tableWidth, rowHeight, 'S');

    let x = left;
    const textY = y + 5;
    doc.text(nameLines, x + 2, textY);
    x += columns[0].width;
    doc.text(String(line.quantity), x + columns[1].width - 2, textY, { align: 'right' });
    x += columns[1].width;
    doc.text(formatMoney(line.unitPrice, invoice.currency), x + columns[2].width - 2, textY, { align: 'right' });
    x += columns[2].width;
    doc.text(`${line.taxPercent}%`, x + columns[3].width - 2, textY, { align: 'right' });
    x += columns[3].width;
    doc.text(formatMoney(line.lineTotal, invoice.currency), x + columns[4].width - 2, textY, { align: 'right' });
    y += rowHeight;
  });

  y += 8;
  const totals: Array<[string, string]> = [
    [labels.subtotal, formatMoney(invoice.subtotal, invoice.currency)],
    [labels.taxTotal, formatMoney(invoice.taxTotal, invoice.currency)],
    [labels.total, formatMoney(invoice.total, invoice.currency)],
  ];
  if (invoice.documentType === 'invoice') {
    totals.push([labels.paid, formatMoney(invoice.paidTotal, invoice.currency)]);
    if ((invoice.overpaid ?? 0) > 0) {
      totals.push([labels.overpaid, formatMoney(invoice.overpaid, invoice.currency)]);
    } else {
      totals.push([labels.remaining, formatMoney(invoice.remaining, invoice.currency)]);
    }
  }

  const totalsWidth = 70;
  const totalsX = right - totalsWidth;
  totals.forEach(([label, value]) => {
    ({ y } = ensureSpace(doc, y, 7, bottomMargin));
    const isEmphasis = label === labels.total || label === labels.remaining || label === labels.overpaid;
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, isEmphasis ? 'bold' : 'normal');
    doc.setFontSize(isEmphasis ? 11 : 9);
    doc.setTextColor(...(isEmphasis ? INK : MUTED));
    doc.text(label, totalsX, y);
    doc.setTextColor(...INK);
    doc.text(value, right, y, { align: 'right' });
    y += 7;
  });

  if (invoice.customerNote?.trim()) {
    y += 4;
    ({ y } = ensureSpace(doc, y, 16, bottomMargin));
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(labels.customerNote.toUpperCase(), left, y);
    y += 5;
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    const noteLines = doc.splitTextToSize(invoice.customerNote.trim(), contentWidth) as string[];
    noteLines.forEach(line => {
      ({ y } = ensureSpace(doc, y, lineHeight, bottomMargin));
      doc.text(String(line), left, y);
      y += lineHeight;
    });
  }

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`${labels.page} ${page} ${labels.of} ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  }

  return { doc, fileName: sanitizeInvoicePdfFileName(invoice.number, invoice.id) };
};

export const buildInvoicePdfBlob = async (invoice: Invoice, labels: InvoicePdfLabels, statusLabel: string) => {
  const { doc, fileName } = await renderInvoicePdf(invoice, labels, statusLabel);
  return { blob: doc.output('blob') as Blob, fileName };
};

export const exportInvoiceToPdf = async (invoice: Invoice, labels: InvoicePdfLabels, statusLabel: string) => {
  const { doc, fileName } = await renderInvoicePdf(invoice, labels, statusLabel);
  doc.save(fileName);
};
